import { Ban } from 'lucide-react'
import { useState } from 'react'

import { useAuth } from '../contexts/auth-context'
import { tt } from '../i18n'
import { parseMoneyToCents } from '../money'

import { FISKAL_STORNO } from './auth-action'
import { EmployeeAuthModal, type AuthSuccessPayload } from './EmployeeAuthModal'
import { mayStornoFiskal, type FiskalFacts } from './fiskal-storno'

/**
 * *Storno fiškál* wherever an order is looked at - `FRONTEND.md` §9.442, FB-761.
 *
 * **The owner asked for it in five places and it existed in one.** `lists/OrderRow.tsx` had the
 * button, the rule and the modal; the dispatch board, *Otvorené stoly*, the payment dialog and the
 * table map had none. This is that wiring in one place rather than four copies of it -
 * `agos-one-guard-spelled-twice`, and the copies would each own a rule about reversing a registered
 * sale.
 *
 * **It renders nothing when a storno is not available**, which is the owner's second message:
 * *"Instead of disabling it, hide it. So its invisible, and show it only when storno for fiškál is
 * available"* - superseding their own *"make the button disabled"* from the message before.
 *
 * **The permission is checked here too**, not only the receipt's state. `POST /fiskal/storno` is
 * guarded `@RequirePermission({"fiskal", "fiskal_storno"})`, and a button that 403s is worse than no
 * button.
 *
 * **The credentials are the modal's job and that is the point of reusing it.** The endpoint
 * re-authenticates whoever authorises the reversal - *"the person at the till is not necessarily the
 * person entitled to reverse a registered sale, and the reversal carries their name"* - so the
 * button never calls the endpoint itself.
 */
export function FiskalStornoAction({
  order,
  onDone,
  className,
  label,
  compact,
}: {
  order: FiskalFacts & { id?: number; orderNumber?: string; total?: number | string }
  /** Called after a successful reversal, with the server's own body, so the caller can refresh. */
  onDone: (response?: AuthSuccessPayload) => void
  className?: string
  /** Defaults to *Storno fiškál*, which is the owner's own wording for the short surfaces. */
  label?: string
  /** No icon, for a row of small buttons. */
  compact?: boolean
}) {
  const { hasPermission } = useAuth()
  const [asking, setAsking] = useState(false)

  // Legacy spells this `… || isMainAdmin` and does not need to: `hasPermission` already answers true
  // for a main_admin without consulting the matrix. Kept identical to `OrderRow`'s line.
  const permitted = hasPermission('fiskal') || hasPermission('fiskal_storno')
  if (!permitted || !mayStornoFiskal(order)) return null

  return (
    <>
      <button
        type="button"
        data-fiskal-storno={String(order.id ?? 0)}
        onClick={(event) => {
          // The surfaces that carry this put it inside a clickable row or card - the board's order
          // card opens a detail, the table map's panel selects. Without this, asking to reverse a
          // receipt also navigates.
          event.stopPropagation()
          setAsking(true)
        }}
        title={tt('Stornovať fiškálny doklad')}
        className={
          className ??
          'flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20 transition-all'
        }
      >
        {!compact && <Ban className="w-3.5 h-3.5" />}
        {label ?? tt('Storno fiškál')}
      </button>
      {asking && (
        <EmployeeAuthModal
          meta={{
            orderId: order.id ?? 0,
            orderNumber: order.orderNumber ?? '',
            orderTotalCents: parseMoneyToCents(order.total ?? 0),
            newStatus: FISKAL_STORNO,
          }}
          onClose={() => setAsking(false)}
          onSuccess={(_confirmedBy, response) => {
            setAsking(false)
            onDone(response)
          }}
        />
      )}
    </>
  )
}
